import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, X } from "lucide-react";
import { LegalLayout } from "../components/LegalLayout";
import { PRODUCTS, ALL_PRODUCTS } from "../data/products";

const ALL = "Alle Produkte";

export default function Products() {
  const [active, setActive] = useState(ALL);
  const [query, setQuery] = useState("");

  const trimmed = query.trim().toLowerCase();

  const items = useMemo(() => {
    let list = active === ALL ? ALL_PRODUCTS : ALL_PRODUCTS.filter((p) => p.category === active);
    if (trimmed) {
      list = ALL_PRODUCTS.filter((p) => p.name.toLowerCase().includes(trimmed));
    }
    return list;
  }, [active, trimmed]);

  const tabs = [ALL, ...PRODUCTS.map((g) => g.category)];

  return (
    <LegalLayout
      eyebrow="Shop"
      title="Products"
      subtitle="Hochwertige Kosmetikprodukte von FUTURACONTOUR® – erhältlich in unserem Studio in Klosters. Gerne beraten wir Sie persönlich zur passenden Pflege für Ihre Haut."
    >
      <div>
        {/* Search bar */}
        <div className="relative max-w-md mb-8" data-testid="product-search-wrap">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999] pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Produkt suchen …"
            data-testid="product-search-input"
            className="w-full pl-11 pr-10 py-3 bg-white border border-[#E5E5E5] rounded-full text-sm text-[#1A1A1A] placeholder:text-[#999] focus:outline-none focus:border-[#3d7d83] transition-colors duration-300"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              data-testid="product-search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999] hover:text-[#1A1A1A] transition-colors"
              aria-label="Suche zurücksetzen"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Category tabs (filters) */}
        <div className={`flex flex-wrap gap-3 ${trimmed ? "opacity-40 pointer-events-none" : ""}`} data-testid="product-tabs">
          {tabs.map((cat) => (
            <button
              key={cat}
              onClick={() => setActive(cat)}
              data-testid={`product-tab-${cat}`}
              className={`px-5 py-2.5 rounded-full text-sm tracking-wide transition-all duration-300 ${
                active === cat
                  ? "bg-[#1A1A1A] text-white"
                  : "border border-[#E5E5E5] text-[#1A1A1A] hover:border-[#C5A059]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Result count */}
        <p className="text-sm text-[#999] mt-6" data-testid="product-result-count">
          {trimmed
            ? `${items.length} Ergebnis${items.length === 1 ? "" : "se"} für „${query.trim()}“`
            : `${items.length} Produkte`}
        </p>

        {/* Product grid */}
        {items.length === 0 ? (
          <div className="mt-12 text-center py-16 border border-dashed border-[#E5E5E5]" data-testid="product-empty">
            <p className="font-playfair text-2xl text-[#1A1A1A] mb-2">Keine Produkte gefunden</p>
            <p className="text-[#666666]">Versuchen Sie einen anderen Suchbegriff.</p>
          </div>
        ) : (
          <div
            className="grid grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8 mt-8"
            data-testid="product-grid"
          >
            {items.map((item) => (
              <Link
                key={item.slug}
                to={`/products/${item.slug}`}
                data-testid={`product-item-${item.slug}`}
                className="group bg-white border border-[#E5E5E5] hover:border-[#C5A059] transition-colors duration-300 flex flex-col"
              >
                <div className="aspect-square bg-white overflow-hidden flex items-center justify-center p-4">
                  <img
                    src={item.image}
                    alt={item.name}
                    loading="lazy"
                    className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4 md:p-5 border-t border-[#F0EEE9] flex-1 flex flex-col">
                  <p className="text-[#1A1A1A] text-sm md:text-base leading-snug min-h-[2.5rem]">
                    {item.name}
                  </p>
                  <p className="font-playfair text-lg text-[#C5A059] mt-2">
                    {item.price}
                    <span className="ml-1.5 text-[10px] tracking-wider uppercase text-[#999] font-sans align-middle">UVP</span>
                  </p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-xs tracking-wider uppercase text-[#3d7d83] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Details <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Note + CTA */}
      <div className="bg-gradient-to-br from-[#1A1A1A] via-[#173c40] to-[#1A1A1A] text-white p-10 md:p-14 text-center mt-16">
        <p className="font-cormorant italic text-2xl md:text-3xl mb-4">
          Interesse an unseren Produkten?
        </p>
        <p className="text-white/70 max-w-xl mx-auto mb-8">
          Alle Produkte sind in unserem Studio in Klosters erhältlich. Kontaktieren
          Sie uns für eine persönliche Beratung oder Reservierung.
        </p>
        <Link
          to="/#contact"
          data-testid="products-cta"
          className="inline-block bg-[#C5A059] text-[#1A1A1A] px-8 py-4 rounded-full text-sm tracking-wider uppercase font-medium hover:bg-[#d4b06a] transition-colors duration-300"
        >
          Kontakt aufnehmen
        </Link>
      </div>
    </LegalLayout>
  );
}
